<template>
  <div class="order-detail" aria-live="polite">
    <div v-if="loading"><font-awesome-icon icon="spinner" class="mr-1" />{{ i18n('label', 'components.loader') }}</div>
    <div v-else-if="error" class="order-detail-error">{{ i18n('error') }}</div>
    <div v-else-if="detailItems.length">
      <b-container>
        <b-table-lite
          :fields="detailFields"
          :items="detailItems"
          caption-top
          fixed
          small
          :aria-label="$t('white-label.widgets.orderStatus.accountHeader.orderDetails')"
        >
          <template #table-caption v-if="strategy != 'SPL'">{{ `${i18n('strategy')}: ${strategyText}` }}</template>
          <template #head()="data">
            <span>{{ i18n(data.field.key) }}</span>
          </template>
          <template #cell(leg)="data">
            <span :class="`leg-${data.index + 1}`">{{ data.value }}</span>
          </template>
        </b-table-lite>
        <b-table-lite
          :fields="activityFields"
          :items="activityItems"
          fixed
          small
          :aria-label="$t('white-label.widgets.orderStatus.accountHeader.orderDetails')"
        >
          <template #head()="data">{{ i18n(data.field.key) }}</template>
          <template #head(placeholder)>{{ '' }}</template>
          <template #cell(leg)="data">
            <span :class="`leg-${data.item.orderIndex + 1}`">{{ data.value }}</span>
          </template>
          <template #cell(activityType)="data">
            <span :class="activityClass(data.item.activity.key)">{{ data.item.activity.value }}</span>
          </template>
        </b-table-lite>
      </b-container>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import API from '@state/td';
import { mapGetters } from 'vuex';
import { camelCase } from 'lodash-es';
import orderUtil from '@mixins/tdOrdersUtils.js';
import { StrategyService } from '@/utils/optionChainServices/strategyService';
import { OptionLeg, StockLeg, OPTION_LEG_ACTION, STOCK_LEG_ACTION } from '@/utils/optionChainServices/legService';
import moment from 'moment-timezone';

export default Vue.extend({
  name: 'order-detail',
  mixins: [orderUtil],
  data() {
    return {
      orderKey: null,
      orderDetails: null,
      loading: null,
      error: false,
      endPoint: null,
      destroyed: false
    };
  },
  beforeMount() {
    this.endPoint = this.params.context.isHistorical
      ? '/waw/brk/wb/services/rest/v3/orders/historical/'
      : '/waw/brk/wb/services/rest/v3/orders/';
    this.orderKey = this.params.data.orderKey;
    this.fetchOrderDetails();
    this.setOrderDetailHeight(25); //set initial height
  },
  computed: {
    orders() {
      return this.orderDetails ? this.orderDetails.order : [];
    },
    detailFields() {
      const fields = [
        ...(this.displayLeg ? ['leg'] : []),
        'referenceNumber',
        'account',
        'description',
        'dateTime',
        ...(this.canDisplayDuration ? ['goodTillDuration'] : [])
      ];
      return fields.map(field => {
        return {
          key: field,
          thClass: field === 'description' ? 'lg-col' : 'sm-col'
        };
      });
    },
    activityFields() {
      const fields = [
        ...(this.displayLeg ? ['leg'] : []),
        'activityType',
        'orderFillQuantity',
        'orderFillPrice',
        'dateTime',
        ...(this.canDisplayDuration ? ['placeholder'] : [])
      ];
      return fields.map(field => {
        return {
          key: field,
          thClass: field === 'orderFillPrice' ? 'lg-col' : 'sm-col'
        };
      });
    },
    detailItems() {
      return this.orders.map((order, index) => {
        return {
          leg: this.i18n('leg' + (index + 1)),
          referenceNumber: order.orderNumber,
          account: this.accountInfo,
          description: this.getInfoText(order),
          dateTime: this.dateTime,
          ...(this.canDisplayDuration ? { goodTillDuration: this.getGoodTillDuration(order) } : {})
        };
      });
    },
    activityItems() {
      const items = this.orders.flatMap((order, index) => {
        return this.getOrderFills(order).map(orderFill => {
          return {
            orderIndex: index,
            leg: this.i18n('leg' + (index + 1)),
            activity: orderFill.activity,
            orderFillQuantity: orderFill.quantity,
            orderFillPrice: orderFill.price,
            dateTime: orderFill.fillTime
          };
        });
      });
      return items;
    },
    accountInfo() {
      const acctInfo = this.orderDetails.accountInfo;
      if (acctInfo) {
        const account = this.accounts.find(account => account.id === acctInfo.accountId);
        return account
          ? `${account.number} - ${account.currency} ${this.i18n('accountType.' + account.type.acctType)}`
          : '';
      }
    },
    strategy() {
      return this.orderDetails ? this.orderDetails.strategy : '';
    },
    strategyText() {
      if (this.orderDetails) {
        if (this.strategy === 'MLO') {
          return this.getMLOStrategy();
        } else if (this.strategy !== 'SPL') {
          const key = this.strategy.toLowerCase();
          return this.i18n(key);
        }
      }
    },
    displayLeg() {
      return ['MLO', 'OCO', 'OTA', 'FTO'].includes(this.strategy);
    },
    dateTime() {
      return this.orderDetails ? this.formatDateTime(this.orderDetails.orderSubmissionDate) : '-';
    },
    canDisplayDuration() {
      const durations = ['DATE', 'CANCEL'];
      if (this.strategy === 'MLO') {
        const extendedTerms = this.orderDetails.multilegEntry ? this.orderDetails.multilegEntry.extendedTerms : null;
        if (extendedTerms) return durations.includes(extendedTerms.goodTill);
      } else {
        return this.orders.some(order => {
          return order.extendedTerms ? durations.includes(order.extendedTerms.goodTill) : false;
        });
      }

      return false;
    },
    ...mapGetters({ accounts: 'accounts/getOrderStatusAccountData' }),
    computedTZ() {
      return this.$store.state.applicationSettings.general.settings.timezone;
    }
  },
  methods: {
    i18n(subkey, ns) {
      return this.$t(`white-label.${ns || 'widgets.orderStatus.detail'}.${subkey}`);
    },
    fetchOrderDetails() {
      this.loading = true;
      if (this.orderKey) {
        API.get(`${this.endPoint}${this.orderKey}/detail?requestFills=true`)
          .then(response => {
            if (!this.destroyed) {
              if (response.data.payload) this.orderDetails = response.data.payload.orderTerms;
              else this.error = true;
              this.loading = false;
              this.setOrderDetailHeight();
            }
          })
          .catch(error => {
            console.log(error);
            this.error = true;
            this.loading = false;
            this.setOrderDetailHeight();
          });
      }
    },
    getMLOStrategy() {
      if (this.strategy === 'MLO') {
        const [leg1, leg2] = this.orders;
        const firstLeg = this.createLegObject(leg1);
        const secondLeg = this.createLegObject(leg2);
        const strategy = StrategyService.detectOptionStrategy(firstLeg, secondLeg);
        return this.i18n(this._.camelCase(strategy), 'widgets.orderEntry.strategy.types');
      }
      return '';
    },
    createLegObject(leg) {
      if (leg.security.type === 'OPTION') {
        return this.createOptionLeg(leg);
      } else {
        return this.createStockLeg(leg);
      }
    },
    createOptionLeg(leg) {
      let action = leg.extendedTerms.orderAction;
      action = OPTION_LEG_ACTION[action] || OPTION_LEG_ACTION.BUY_TO_OPEN;
      const quantity = leg.quantity;
      const optionLeg = {};
      if (leg.security.extendedTerms) {
        optionLeg['xopt._TYPE_CP'] = leg.security.extendedTerms.type.toLowerCase() === 'call' ? 'C' : 'P';
        optionLeg['xopt._MATURITY_ISO'] = leg.security.extendedTerms.contractExpiry;
        optionLeg['xopt._STRIKE'] = leg.security.extendedTerms.strikePrice;
        optionLeg['q.LOT_SIZE_A'] = this.params.data['q.LOT_SIZE_A'] || 1;
      }

      return new OptionLeg(action, quantity, optionLeg);
    },
    createStockLeg(leg) {
      let action = leg.extendedTerms.orderAction;
      action = STOCK_LEG_ACTION[action] || STOCK_LEG_ACTION.BUY;
      const quantity = leg.quantity;
      return new StockLeg(action, quantity, leg);
    },
    async setOrderDetailHeight(height) {
      await this.$nextTick();
      this.params.node.setRowHeight(height || this.$el.scrollHeight);
      if (this.params.node.gridApi) this.params.node.gridApi.onRowHeightChanged();
    },
    activityType(order) {
      return order.extendedTerms ? order.extendedTerms.orderAction : '-';
    },
    getOrderFills(order) {
      const orderFills = [];
      const extendedTerms = order.extendedTerms;
      const isNotional = !!order.dollarAmt;
      const formatFillPrice = value => {
        if (value && value !== '') {
          return this.$f(value, 'float.price');
        }
        return this.strategy === 'MLO' ? '' : this.i18n('MARKET', 'widgets.orderStatus.priceTypes');
      };
      //first entry
      let quantity = isNotional ? order.dollarAmt : order.quantity;

      orderFills.push({
        activity: {
          key: extendedTerms.orderAction,
          value: this.i18n(camelCase(extendedTerms.orderAction), 'widgets.orderEntry.actions')
        },
        price: formatFillPrice(extendedTerms.limitPrice || extendedTerms.triggerPrice),
        quantity: `${isNotional ? '$' : ''}${this.$f(quantity, 'float.fractionalQuantity')}`,
        fillTime: this.formatDateTime(this.orderDetails.orderSubmissionDate)
      });
      //fill entries
      quantity = isNotional && order.orderStatus === 'FILLED' ? order.details.totalFilledQuantity : quantity;
      if (extendedTerms.orderFill) {
        //As per mm2 implementation, orderfills needs to be sorted in ascending to calculate part fills
        extendedTerms.orderFill
          .slice(0)
          .reverse()
          .forEach(orderFill => {
            let fillQuantity = orderFill.quantity;
            if (isNotional && order.orderStatus !== 'FILLED') {
              fillQuantity = order.details.totalFilledDollarAmt;
            }
            quantity = Number((quantity - fillQuantity).toFixed(5));

            let activityValue = '-';
            if (quantity > 0) activityValue = this.i18n('partFill');
            else if (quantity === 0) activityValue = this.i18n('fill');

            orderFills.push({
              activity: {
                key: 'fill',
                value: activityValue
              },
              price: formatFillPrice(orderFill.price),
              quantity: this.$f(orderFill.quantity, 'float.fractionalQuantity'),
              fillTime: this.formatDateTime(orderFill.fillDate)
            });
          });
      }
      return orderFills.reverse();
    },
    getGoodTillDuration(order) {
      let gtd;
      if (this.strategy === 'MLO') {
        const extendedTerms = this.orderDetails.multilegEntry ? this.orderDetails.multilegEntry.extendedTerms : null;
        if (extendedTerms) gtd = moment(extendedTerms.goodTillDate).tz(this.computedTZ);
      } else {
        gtd = order.extendedTerms ? moment(order.extendedTerms.goodTillDate).tz(this.computedTZ) : null;
      }
      return this.formatDateTime(gtd, 'L');
    },
    activityClass(key) {
      key = key.toLowerCase();
      if (key.includes('buy')) return 'details-buy';
      else if (key.includes('sell')) return 'details-sell';
    },
    formatDateTime(value, format) {
      if (value && value !== '' && value !== '-') {
        return moment(value)
          .tz(this.computedTZ)
          .format(format || 'L LTS');
      }
      return '-';
    }
  },
  destroyed() {
    this.destroyed = true;
  }
});
</script>

<style lang="scss">
.order-detail {
  background-color: $gray-800;
  .container {
    padding: 0.5em;
  }
  table {
    padding-bottom: 5px;
    thead th {
      .theme--light & {
        background-color: $gray-400;
      }
    }

    th,
    td {
      white-space: pre-wrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    > caption {
      background-color: transparent !important;
      color: $white !important;
      padding-top: unset !important;
    }

    td {
      .leg-1 {
        color: $cyan-text;
      }
      .leg-2 {
        color: $purple-text-more-contrast;
      }
      .leg-3 {
        color: $negative-text;
      }
      .details-buy {
        color: $positive !important;
      }
      .details-sell {
        color: $negative-text !important;
      }
    }

    th.sm-col {
      width: 7%;
    }
    th.lg-col {
      width: 15%;
    }
  }
  .order-detail-error {
    margin-top: 3px;
    height: 25px;
  }
}
</style>
